import _ from 'lodash';
import React, { Component } from 'react';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import ServicePicker from 'components/ui/servicePicker/ServicePicker';
import TagTextFilter from 'components/ui/filters/TagTextFilter';
import { filterMockServices } from 'store/mockServices/mockServiceActions';
import { setPageFilter } from 'store/ui/pageFilters/pageFilterActions';
import { Service, TransactionType } from 'types/rest/assetCatalogTypes';
import { toggleOpenDDPanel } from '../../../store/ui/ddActions';
import DataDefinitionButton from '../../tdm/DataDefinitionButton/DataDefinitionButton';
import { isFeatureEnabled, isPrivateCloud, isRoleWithPermissions } from '../../../utils/bzmUserConfigUtil';
import { createLinkToModel } from '../../../store/dataModel/dataModelActions';
import { DataProfilingWizardButton } from '@blazemeter/blaze-data-ui';
import './DataProfiler.css';
import TypeFilter from './TypeFilter/TypeFilter';

interface PageFilterProps {
  setFilter: (serviceId: number, searchTerm: string, txType?: TransactionType) => void;
  services: Service[];
  context: 'transactions' | 'mockServices' | 'assets';
  /** Indicating whether full text search is enabled / disabled */
  isSearchDisabled?: boolean;
  placeholder?: string;
  dataPanelButtonEnabled?: boolean;
  /**
   * Component which will be rendered in the middle of the page filter
   */
  middleComponent?: any;
  showServicePickerLabel?: boolean;
  onCreateBlueprint?: (service: Service) => void;
}

type AllProps = MapToStateProps & MapDispatchToProps & PageFilterProps;

interface PageFilterState {
  serviceId: number;
  searchTerm: string;
  showHttpType: boolean;
  showMessagingType: boolean;
}

/**
 * Main top level filter by Service and Tag/Text for the Virtual Services and Transactions pages
 */
class PageFilter extends Component<AllProps> {
  state: PageFilterState = {
    serviceId: -1,
    searchTerm: '',
    showHttpType: true,
    showMessagingType: true,
  };

  constructor(props: AllProps) {
    super(props);
  }

  componentDidMount() {
    // Override activeVirtualService if localStorage holds its outdated value
    const servicesIds = this.props.services.map((service: Service) => {
      return service.id;
    });

    const serviceId = servicesIds.includes(this.props.activeVirtualService) ? this.props.activeVirtualService : 0;

    const searchTerm = this.props.context === 'mockServices' ? this.props.selectedTagVal : '';
    // Reset search filter immediately on mount
    this.props.setPageFilter({
      serviceId: serviceId,
      searchTerm: searchTerm,
      txType: this.props.txType,
    });

    // Re-use service id from the filter and virtual service types from the filter
    // reset search term on mount
    this.setState({
      serviceId: serviceId,
      searchTerm: '',
      showHttpType: this.props.txType === 'HTTP' || this.props.txType === undefined,
      showMessagingType: this.props.txType === 'MESSAGING' || this.props.txType === undefined,
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<AllProps>): void {
    // Override local state with the values from the store
    if (
      nextProps.activeVirtualService !== this.state.serviceId ||
      nextProps.currentSearchTerm !== this.state.searchTerm
    ) {
      this.setState({
        serviceId: nextProps.activeVirtualService,
        searchTerm: nextProps.selectedTagVal,
      });
      if (!_.isEmpty(nextProps.selectedTagVal)) {
        this.setState({ currentSearchTerm: nextProps.selectedTagVal });
      }
    }
  }

  handleSearchTermChange = (searchTerm: string) => {
    this.setState(
      {
        searchTerm: searchTerm,
      },
      () => {
        this.updateStoreAndPropagateSearchTerm();
      },
    );
  };

  // Update of store is triggered with delay
  updateStoreAndPropagateSearchTerm = _.debounce(() => {
    // Store in redux
    this.props.setPageFilter({
      serviceId: this.state.serviceId,
      searchTerm: this.state.searchTerm,
    });

    // Propagate
    this.props.setFilter(this.state.serviceId, this.state.searchTerm);
  }, 750);

  handleServiceChange = (service: Service | null) => {
    const selectedId = service === null ? 0 : service.id === undefined ? 0 : service.id;
    this.setState(
      {
        serviceId: selectedId,
      },
      () => {
        // Store in redux
        this.props.setPageFilter({
          serviceId: this.state.serviceId,
          searchTerm: this.state.searchTerm,
        });

        // Propagate
        this.props.setFilter(this.state.serviceId, this.state.searchTerm);
      },
    );
  };

  handleTransactionTypeChange = (type: TransactionType) => {
    this.setState(
      {
        showHttpType: type === 'HTTP' ? !this.state.showHttpType : this.state.showHttpType,
        showMessagingType: type === 'MESSAGING' ? !this.state.showMessagingType : this.state.showMessagingType,
      },
      () => {
        let filterByTxType: TransactionType = undefined;
        if (this.state.showHttpType) {
          filterByTxType = this.state.showMessagingType ? undefined : 'HTTP';
        } else if (this.state.showMessagingType) {
          filterByTxType = 'MESSAGING';
        }
        // Propagate
        this.props.setFilter(this.state.serviceId, this.state.searchTerm, filterByTxType);
      },
    );
  };

  handleDDPanelToggle = () => {
    this.props.toggleOpenDDPanel();
    if (this.state.serviceId !== 0) {
      this.props.createLinkToModel(this.state.serviceId);
    }
  };

  render() {
    return (
      <div className={'row flex-nowrap'}>
        <div className={'col d-flex justify-content-between'}>
          {/*<Form inline onSubmit={(e: any) => e.preventDefault()}>*/}

          <label className="mt-2 mr-3 font-weight-bold" htmlFor="serviceFilter" aria-describedby="servicePickerDiv">
            Service:
          </label>
          <div id="servicePickerDiv" className="mr-4 d-inline-block" style={{ minWidth: '200px' }}>
            <ServicePicker
              selectionDisabled={this.props.isLoadingDataModel}
              preSelected={this.state.serviceId}
              selectHandler={this.handleServiceChange}
              isSelectAll={true}
              isCreateEnabled={true}
              id="serviceFilter"
              isMaintenanceEnabled={this.props.isRoleWithEditPermissions}
              isCloneEnabled={true}
              onCreateBlueprint={this.props.onCreateBlueprint}
            />
          </div>

          <div id="transactionTypeFilter" className="mr-4 d-inline-block">
            <TypeFilter
              handleChange={(value: TransactionType) => {
                this.handleTransactionTypeChange(value);
              }}
              selected={{ messaging: this.state.showMessagingType, http: this.state.showHttpType }}
            />
          </div>

          <div id="additional" className="ml-auto">
            {this.props.middleComponent !== undefined && this.props.middleComponent}
          </div>
        </div>
        <div className={'col-auto'}>
          <div style={{ display: 'inline-block', maxWidth: '200px' }}>
            {!this.props.isSearchDisabled && (
              <TagTextFilter
                className={'ml-auto'}
                searchTerm={this.state.searchTerm}
                setSearchTerm={this.handleSearchTermChange}
                context={this.props.context}
                placeholder={this.props.placeholder}
              />
            )}
          </div>
          <div style={{ display: 'inline-block' }}>
            {this.props.dataPanelButtonEnabled && !this.props.privateData && (
              <div className="btn-group">
                <DataDefinitionButton
                  onClick={this.handleDDPanelToggle}
                  paramCount={this.props.paramCount}
                  isLoading={!!this.props.isLoadingDataModel}
                />
                {this.props.isDDPanelOpen &&
                  !this.props.dataModel.isLoading &&
                  this.props.dataModel.isLinked &&
                  this.props.activeVirtualService > 0 && <DataProfilingWizardButton />}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ ui, services, mockservices, marFiles, blazeUserConfig, dataModel }: ApplicationState) => ({
  activeVirtualService: ui.pageFilter.serviceId,
  currentSearchTerm: ui.pageFilter.searchTerm,
  txType: ui.pageFilter.txType,
  services: services.services,
  mockServicesIsLoading: mockservices.isLoading,
  selectedTagVal: marFiles.selectedTagVal,
  privateData: isPrivateCloud(blazeUserConfig.blazeUserConfig),
  paramCount: dataModel ? dataModel.paramCount + _.size(dataModel.globalEntity) : 0,
  isLoadingDataModel: dataModel ? dataModel.isLoading : false,
  isRoleWithEditPermissions: isRoleWithPermissions(blazeUserConfig.blazeUserConfig),
  isDDPanelOpen: ui.isDDPanelOpened,
  dataModel,
});

const mapDispatchToProps = {
  setPageFilter,
  filterMockServices,
  toggleOpenDDPanel,
  createLinkToModel,
};

type MapToStateProps = ReturnType<typeof mapStateToProps>;
type MapDispatchToProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(PageFilter);
